import clsx from 'clsx'
import * as React from 'react'
import { Logger } from 'src/3rds/cube'
import { classToFComponent, FCClassContext } from 'src/3rds/cube-react'

import { makeStyles } from 'tss-react/mui'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ButtonBase from '@mui/material/ButtonBase'
import Card from '@mui/material/Card'
import LinearProgress from '@mui/material/LinearProgress'
import Typography from '@mui/material/Typography'

import BlockIcon from '@mui/icons-material/Block'
import CampaignIcon from '@mui/icons-material/Campaign'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'
import HubIcon from '@mui/icons-material/Hub'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import InsightsIcon from '@mui/icons-material/Insights'
import NorthEastIcon from '@mui/icons-material/NorthEast'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'
import RefreshIcon from '@mui/icons-material/Refresh'
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined'
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

import { acompanhaAlertas, NOME_DO_DESTINO, ROTULO_DO_NIVEL, valorDoNumero } from '../overview.regras'
import type { Alerta, Atalho, Destino, NivelAlerta, Numero } from '../overview_scopes'
import { OverviewScope } from '../overview_scopes'

const LOG = Logger.get('Overview-VIEW')

const ICONE_DO_DESTINO: Record<Destino, React.ComponentType<{ className?: string }>> = {
    dashboards: InsightsIcon,
    campanha: CampaignIcon,
    cadastroLote: PlaylistAddCheckIcon,
    grupoManual: HubIcon,
    bloqueios: BlockIcon,
    templates: TextSnippetOutlinedIcon,
    mensagemDireta: ForumOutlinedIcon,
    simulador: ScienceOutlinedIcon
}

const ICONE_DO_NIVEL: Record<NivelAlerta, React.ComponentType<{ className?: string }>> = {
    erro: ErrorOutlineIcon,
    atencao: WarningAmberIcon,
    info: InfoOutlinedIcon
}

const useStyles = makeStyles()((theme) => {
    const cor: Record<NivelAlerta, string> = {
        erro: theme.palette.error.main,
        atencao: theme.palette.warning.main,
        info: theme.palette.info.main
    }
    return {
        view: {
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            padding: theme.spacing(3),
            rowGap: theme.spacing(3),
            // O body do portal é fixo em 100vh, então o módulo precisa rolar por conta própria.
            overflow: 'auto',
            minHeight: 0,
            backgroundColor: theme.palette.mode === 'dark' ? undefined : '#f8fafc',
            '& > *': { flexShrink: 0 }
        },
        cabecalho: {
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: theme.spacing(2),
            flexWrap: 'wrap'
        },
        data: {
            color: theme.palette.text.secondary,
            fontSize: 15,
            '&::first-letter': { textTransform: 'uppercase' }
        },
        secao: {
            fontWeight: 600,
            fontSize: 13,
            letterSpacing: 0.6,
            textTransform: 'uppercase',
            color: theme.palette.text.secondary,
            marginBottom: theme.spacing(1)
        },
        card: {
            borderRadius: 14,
            boxShadow: '0px 2px 12px rgba(0,0,0,0.05)'
        },
        alerta: {
            display: 'flex',
            gap: theme.spacing(1.5),
            alignItems: 'flex-start',
            // Em tela estreita o botão desce para baixo do texto, em vez de espremê-lo.
            flexWrap: 'wrap',
            padding: theme.spacing(1.75, 2),
            '& + &': { borderTop: `1px solid ${theme.palette.divider}` }
        },
        alertaTexto: { flex: '1 1 240px', minWidth: 0 },
        alertaNivel: { fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 },
        ...Object.fromEntries(
            (Object.keys(cor) as NivelAlerta[]).map((nivel) => [`nivel_${nivel}`, { color: cor[nivel] }])
        ),
        tudoBem: {
            display: 'flex',
            gap: theme.spacing(1.5),
            alignItems: 'center',
            padding: theme.spacing(2),
            color: theme.palette.success.main
        },
        numeros: {
            display: 'grid',
            gap: theme.spacing(2),
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))'
        },
        numero: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            textAlign: 'left',
            width: '100%',
            height: '100%',
            padding: theme.spacing(2),
            borderRadius: 14
        },
        valor: { fontSize: 32, fontWeight: 700, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' },
        detalhe: { fontSize: 12, color: theme.palette.text.secondary, marginTop: theme.spacing(0.5) },
        atalhos: {
            display: 'grid',
            gap: theme.spacing(2),
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))'
        },
        atalho: {
            display: 'flex',
            gap: theme.spacing(1.5),
            alignItems: 'flex-start',
            textAlign: 'left',
            width: '100%',
            height: '100%',
            padding: theme.spacing(2),
            borderRadius: 14
        },
        iconeAtalho: {
            display: 'flex',
            padding: theme.spacing(1),
            borderRadius: 10,
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eef6ea',
            color: theme.palette.mode === 'dark' ? '#6cea48' : '#2e7d32'
        },
        rodape: {
            marginTop: 'auto',
            paddingTop: theme.spacing(2),
            fontSize: 12,
            color: theme.palette.text.secondary
        }
    }
})

export type OverviewViewProps = {
    className?: string
    scope: OverviewScope
}

class OverviewClass implements FCClassContext<OverviewViewProps> {
    scope!: OverviewScope
    styles!: ReturnType<typeof useStyles>

    // :: emissors

    readonly emitAtualizar = () => this.scope.onAtualizar().catch(LOG.caught)
    readonly emitAbrir = (destino: Destino) => () => this.scope.onAbrir(destino).catch(LOG.caught)

    // :: Methods

    onSyncState({ scope }: OverviewViewProps) {
        this.scope = scope
        this.styles = useStyles()
    }

    render({ className }: OverviewViewProps) {
        const { scope } = this
        const { classes } = this.styles
        const painel = scope.painel

        return (
            <div className={clsx(classes.view, className)}>
                <Box className={classes.cabecalho}>
                    <Box>
                        <Typography variant="h4" fontWeight={700}>
                            {scope.saudacao}
                        </Typography>
                        <Typography className={classes.data}>{scope.hoje}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                        {painel && (
                            <Typography fontSize={12} color="text.secondary">
                                Atualizado às{' '}
                                {new Date(painel.geradoEm).toLocaleTimeString('pt-BR', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })}
                            </Typography>
                        )}
                        <Button
                            size="small"
                            startIcon={<RefreshIcon />}
                            onClick={this.emitAtualizar}
                            disabled={scope.carregando}
                        >
                            Atualizar
                        </Button>
                    </Box>
                </Box>

                {scope.carregando && <LinearProgress sx={{ mt: -2 }} />}
                {scope.erro && <Alert severity="error">{scope.erro}</Alert>}
                {painel && painel.indisponiveis.length > 0 && (
                    <Alert severity="warning">
                        Não carregou agora: {painel.indisponiveis.join(', ')}. O resto do painel está atualizado.
                    </Alert>
                )}

                {painel && acompanhaAlertas(painel.blocos) && this.renderAlertas(painel.alertas)}
                {painel && painel.numeros.length > 0 && this.renderNumeros(painel.numeros)}
                {scope.atalhos.length > 0 && this.renderAtalhos(scope.atalhos)}

                <Typography className={classes.rodape}>© AG Capital. Dados protegidos e monitorados.</Typography>
            </div>
        )
    }

    renderAlertas(alertas: Alerta[]) {
        const { classes } = this.styles
        return (
            <Box>
                <Typography className={classes.secao} component="h2">
                    Precisa de atenção
                </Typography>
                <Card className={classes.card}>
                    {alertas.length === 0 ? (
                        <Box className={classes.tudoBem}>
                            <CheckCircleOutlineIcon />
                            <Typography color="text.primary">Nada pedindo atenção agora.</Typography>
                        </Box>
                    ) : (
                        alertas.map((a) => {
                            const Icone = ICONE_DO_NIVEL[a.nivel]
                            const corDoNivel = (classes as Record<string, string>)[`nivel_${a.nivel}`]
                            return (
                                <Box key={a.id} className={classes.alerta}>
                                    <Icone className={corDoNivel} />
                                    <Box className={classes.alertaTexto}>
                                        <Typography className={clsx(classes.alertaNivel, corDoNivel)}>
                                            {ROTULO_DO_NIVEL[a.nivel]}
                                        </Typography>
                                        <Typography fontWeight={600}>{a.titulo}</Typography>
                                        {a.detalhe && (
                                            <Typography fontSize={14} color="text.secondary">
                                                {a.detalhe}
                                            </Typography>
                                        )}
                                    </Box>
                                    {a.destino && (
                                        <Button
                                            size="small"
                                            endIcon={<NorthEastIcon fontSize="small" />}
                                            onClick={this.emitAbrir(a.destino)}
                                            sx={{ whiteSpace: 'nowrap', flexShrink: 0, marginLeft: 'auto' }}
                                        >
                                            {NOME_DO_DESTINO[a.destino]}
                                        </Button>
                                    )}
                                </Box>
                            )
                        })
                    )}
                </Card>
            </Box>
        )
    }

    renderNumeros(numeros: Numero[]) {
        const { classes } = this.styles
        return (
            <Box>
                <Typography className={classes.secao} component="h2">
                    Hoje
                </Typography>
                <Box className={classes.numeros}>
                    {numeros.map((n) => {
                        const conteudo = (
                            <>
                                <Typography fontSize={14} color="text.secondary">
                                    {n.rotulo}
                                </Typography>
                                <Typography className={classes.valor}>{valorDoNumero(n)}</Typography>
                                {n.detalhe && <Typography className={classes.detalhe}>{n.detalhe}</Typography>}
                            </>
                        )
                        return (
                            <Card key={n.id} className={classes.card}>
                                {n.destino ? (
                                    <ButtonBase
                                        className={classes.numero}
                                        onClick={this.emitAbrir(n.destino)}
                                        aria-label={`${n.rotulo}: ${valorDoNumero(n)}. Abrir ${
                                            NOME_DO_DESTINO[n.destino]
                                        }`}
                                    >
                                        {conteudo}
                                    </ButtonBase>
                                ) : (
                                    <Box className={classes.numero}>{conteudo}</Box>
                                )}
                            </Card>
                        )
                    })}
                </Box>
            </Box>
        )
    }

    renderAtalhos(atalhos: Atalho[]) {
        const { classes } = this.styles
        return (
            <Box>
                <Typography className={classes.secao} component="h2">
                    Módulos
                </Typography>
                <Box className={classes.atalhos}>
                    {atalhos.map((a) => {
                        const Icone = ICONE_DO_DESTINO[a.destino]
                        return (
                            <Card key={a.destino} className={classes.card}>
                                <ButtonBase className={classes.atalho} onClick={this.emitAbrir(a.destino)}>
                                    <Box className={classes.iconeAtalho}>
                                        <Icone />
                                    </Box>
                                    <Box>
                                        <Typography fontWeight={600}>{a.nome}</Typography>
                                        <Typography fontSize={13} color="text.secondary">
                                            {a.descricao}
                                        </Typography>
                                    </Box>
                                </ButtonBase>
                            </Card>
                        )
                    })}
                </Box>
            </Box>
        )
    }
}

export const OverviewView = classToFComponent(OverviewClass, React)
export default OverviewView
